/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { Loader, Table, Label, Button, Icon } from "semantic-ui-react";
import { ClassSchedule } from "../../../../api";
import { useAuth, usePermissions } from "../../../../hooks";
import { BasicModal } from "../../../../components/Shared";
import { ClassScheduleForm } from "../ClassScheduleForm";
import "./ClassScheduleTable.scss";

const classScheduleController = new ClassSchedule();

export function ClassScheduleTable(props) {
  const { gradeId, academicCycle, reload, onReload } = props;
  const { accessToken } = useAuth();
  const { isSuperAdmin } = usePermissions();

  const [classSchedules, setClassSchedules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  // Días de la semana
  const daysOfWeek = [
    "Lunes", "Martes", "Miércoles", "Jueves", "Viernes" //, "Sábado", "Domingo"
  ];

  const generateTimeSlots = (cycle) => {
    if (!cycle) return [];
    const pedagogicalHour = cycle.pedagogicalHour || 50;
    const shiftStart = cycle.shiftStart || "08:00:00";
    const shiftEnd = cycle.shiftEnd || "15:40:00";
    const recessStart = cycle.recessStart || "10:30:00";
    const recessEnd = cycle.recessEnd || "10:45:00";

    const toMinutes = (timeStr) => {
      if (!timeStr) return 0;
      const parts = timeStr.split(":");
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      return h * 60 + m;
    };

    const toTimeStr = (minutes) => {
      const h = Math.floor(minutes / 60).toString().padStart(2, "0");
      const m = (minutes % 60).toString().padStart(2, "0");
      return `${h}:${m}`;
    };

    const format12Hour = (time24) => {
      if (!time24) return "";
      const parts = time24.split(":");
      let h = parseInt(parts[0], 10) || 0;
      const m = parts[1] || "00";
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12;
      h = h ? h : 12; // 0 becomes 12
      return `${h}:${m} ${ampm}`;
    };

    const startMin = toMinutes(shiftStart);
    const endMin = toMinutes(shiftEnd);
    const recStartMin = recessStart ? toMinutes(recessStart) : null;
    const recEndMin = recessEnd ? toMinutes(recessEnd) : null;
    const slotDuration = parseInt(pedagogicalHour, 10) || 50;

    const slots = [];
    let currentMin = startMin;

    while (currentMin < endMin) {
      if (recStartMin !== null && recEndMin !== null && currentMin === recStartMin) {
        slots.push({
          start: toTimeStr(recStartMin),
          end: toTimeStr(recEndMin),
          display: `${format12Hour(toTimeStr(recStartMin))} - ${format12Hour(toTimeStr(recEndMin))}`,
          isClass: false,
          label: "RECREO",
        });
        currentMin = recEndMin;
        continue;
      }

      let nextMin = currentMin + slotDuration;

      if (recStartMin !== null && currentMin < recStartMin && nextMin > recStartMin) {
        nextMin = recStartMin;
      }

      if (nextMin > endMin) {
        nextMin = endMin;
      }

      if (nextMin > currentMin) {
        slots.push({
          start: toTimeStr(currentMin),
          end: toTimeStr(nextMin),
          display: `${format12Hour(toTimeStr(currentMin))} - ${format12Hour(toTimeStr(nextMin))}`,
          isClass: true,
        });
      }

      currentMin = nextMin;
    }

    return slots;
  };

  const timeSlots = academicCycle ? generateTimeSlots(academicCycle) : [
    { start: "08:00", end: "08:50", display: "08:00 AM - 08:50 AM", isClass: true },
    { start: "08:50", end: "09:40", display: "08:50 AM - 09:40 AM", isClass: true },
    { start: "09:40", end: "10:30", display: "09:40 AM - 10:30 AM", isClass: true },
    { start: "10:30", end: "10:45", display: "10:30 AM - 10:45 AM", isClass: false, label: "RECREO" },
    { start: "10:45", end: "11:35", display: "10:45 AM - 11:35 AM", isClass: true },
    { start: "11:35", end: "12:25", display: "11:35 AM - 12:25 PM", isClass: true },
    { start: "12:25", end: "13:15", display: "12:25 PM - 01:15 PM", isClass: true },
    { start: "13:15", end: "14:00", display: "01:15 PM - 02:00 PM", isClass: false, label: "ALMUERZO" },
    { start: "14:00", end: "14:50", display: "02:00 PM - 02:50 PM", isClass: true },
    { start: "14:50", end: "15:40", display: "02:50 PM - 03:40 PM", isClass: true },
  ];

  const onOpenCloseModal = () => {
    setShowModal((prevState) => !prevState);
    setEditingSchedule(null);
  };

  const onEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setShowModal(true);
  };

  const onCreateSchedule = (day, slot) => {
    setEditingSchedule({ scheduledDay: day, startTime: slot.start, endTime: slot.end });
    setShowModal(true);
  };

  // Función para cargar los horarios
  useEffect(() => {
    if (!gradeId || !academicCycle?.id) return;
    
    const loadClassSchedules = async () => {
      try {
        setLoading(true);
        const response = await classScheduleController.getClassSchedules(
          accessToken, 
          gradeId, 
          academicCycle.id
        );
        setClassSchedules(response);
      } catch (error) {
        console.error("Error loading class schedules:", error);
        setClassSchedules([]);
      } finally {
        setLoading(false);
      }
    };

    loadClassSchedules();
  }, [gradeId, academicCycle?.id, reload, accessToken]);

  // Función para organizar horarios por día y hora
  const getScheduleForSlot = (day, timeSlot) => {
    if (!classSchedules) return null;
    
    return classSchedules.find(schedule => {
      if (schedule.scheduledDay !== day) return false;
      
      // Comparar horarios (formato HH:MM)
      const scheduleTime = schedule.startTime?.substring(0, 5); // HH:MM
      return scheduleTime === timeSlot.start;
    });
  };

  // Función para obtener el color de fondo de la celda
  const getCellColor = (schedule) => {
    if (!schedule) return "transparent";
    
    // Colores pasteles sólidos para un excelente contraste con el texto oscuro
    const colors = ["#e3f2fd", "#f3e5f5", "#e8f5e8", "#fff3e0", "#fce4ec", "#f1f8e9", "#e0f2f1"];
    
    const subjectName = schedule.CourseTeacher?.Subject?.name || "";
    const colorIndex = subjectName.length % colors.length;
    return colors[colorIndex];
  };

  // Renderizar contenido de la celda
  const renderCellContent = (schedule) => {
    if (!schedule) return null;

    const subject = schedule.CourseTeacher?.Subject?.name || "Sin materia";
    const teacher = schedule.CourseTeacher?.User 
      ? `${schedule.CourseTeacher.User.firstname} ${schedule.CourseTeacher.User.lastname || ""}`.trim()
      : "Sin profesor";
    const classroom = schedule.classroom || "Sin aula";
    const timeRange = `${schedule.startTime?.substring(0, 5)} - ${schedule.endTime?.substring(0, 5)}`;

    return (
      <div className="schedule-cell-content">
        <div className="subject-name">{subject}</div>
        <div className="teacher-name">Docente: {teacher}</div>
        <div className="classroom">Aula: {classroom}</div>
        {schedule.notes && (
          <div className="notes" title={schedule.notes}>
            📝 {schedule.notes.length > 30 ? `${schedule.notes.substring(0, 30)}...` : schedule.notes}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <Loader active inline="centered" />;

  if (!gradeId || !academicCycle) {
    return (
      <div className="schedule-table-message">
        Selecciona un grado para ver el horario
      </div>
    );
  }

  return (
    <>
      <div className="class-schedule-table">
        <Table celled structured className="schedule-table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className="header" width={2}>Hora</Table.HeaderCell>
              {daysOfWeek.map(day => (
                <Table.HeaderCell className="header" key={day} textAlign="center">
                  {day}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          
          <Table.Body>
            {timeSlots.map(timeSlot => (
              <Table.Row key={timeSlot.start}>
                <Table.Cell className="time-cell">
                  <strong>{timeSlot.display}</strong>
                </Table.Cell>
                {!timeSlot.isClass ? (
                  <Table.Cell 
                    colSpan={daysOfWeek.length}
                    textAlign="center"
                    style={{
                      backgroundColor: timeSlot.label === "RECREO" ? "#fff3cd" : "#d1ecf1",
                      fontWeight: "bold",
                      fontSize: "1.1em",
                      color: timeSlot.label === "RECREO" ? "#856404" : "#0c5460"
                    }}
                  >
                    {timeSlot.label}
                  </Table.Cell>
                ) : (
                  daysOfWeek.map(day => {
                    const schedule = getScheduleForSlot(day, timeSlot);
                    return (
                      <Table.Cell 
                        key={`${day}-${timeSlot.start}`}
                        className="schedule-cell"
                        style={{ 
                          backgroundColor: getCellColor(schedule),
                          cursor: isSuperAdmin ? "pointer" : "default",
                          position: "relative"
                        }}
                        onClick={() => {
                          if (!isSuperAdmin) return;
                          if (schedule) {
                            onEditSchedule(schedule);
                          } else {
                            onCreateSchedule(day, timeSlot);
                          }
                        }}
                      >
                        {renderCellContent(schedule)}
                        {!schedule && isSuperAdmin && (
                          <div className="empty-cell">
                            <Icon name="plus" size="small" />
                            <span>Agregar</span>
                          </div>
                        )}
                      </Table.Cell>
                    );
                  })
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>

      <BasicModal
        size="small"
        show={showModal}
        close={onOpenCloseModal}
        title={editingSchedule?.id ? "Editar horario de clase" : "Crear horario de clase"}
      >
        <ClassScheduleForm
          onClose={onOpenCloseModal}
          onReload={onReload}
          classSchedule={editingSchedule?.id ? editingSchedule : null}
          gradeId={gradeId}
          academicCycleId={academicCycle?.id}
          initialDay={editingSchedule?.scheduledDay}
          initialTime={editingSchedule?.startTime}
          initialEndTime={editingSchedule?.endTime}
        />
      </BasicModal>
    </>
  );
}